import React from 'react';
import { StyleSheet, View, Text, TouchableOpacity, useWindowDimensions } from 'react-native';
import { SkiaCanvas } from './SkiaCanvas';
import { SvgTemplateOverlay } from './SvgTemplateOverlay';
import { IActivity, KeyStage, TracingActivityConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAuthStore } from '../store/useAuthStore';
import { SubmissionEngine } from '../services/SubmissionEngine';
import { useStageTokens } from '../hooks/useStageTokens';

interface TracingActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const TracingActivity: React.FC<TracingActivityProps> = ({ activity, onComplete, keyStage }) => {
    const tokens = useStageTokens(keyStage);
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const config = activity.config as unknown; // Cast to any to support legacy/migration schemas
    const targetText = (config as any).targetText || (config as any).letter || '?';
    const { completedStrokes, clearStrokes } = useStrokeStore();
    const { studentName, classId } = useAuthStore();

    const handleFinish = async () => {
        if (completedStrokes.length > 0) {
            await SubmissionEngine.submitActivity({
                activityId: activity.id,
                studentId: studentName || 'anonymous',
                classId: classId || 'none',
                strokes: completedStrokes
            });

            onComplete();
            // strokes will be cleared by App.tsx when user moves to next activity
        }
    };

    return (
        <View style={styles.container}>
            <View style={[styles.header, isLandscape && styles.landscapeHeader]}>
                <View style={styles.headerRow}>
                    <Text style={[styles.title, { fontSize: tokens.titleFontSize }, isLandscape && styles.landscapeTitle]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                </View>
                {!isLandscape && <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>}
            </View>

            <View style={styles.canvasWrapper}
                accessibilityRole="image"
                accessibilityLabel={`Handwriting canvas for tracing the letter ${targetText}`}
            >
                <SkiaCanvas />
                <SvgTemplateOverlay
                    svgTemplateUrl={(config as TracingActivityConfig).svgTemplateUrl}
                    targetText={targetText}
                />
            </View>

            <View style={[styles.footer, isLandscape && styles.landscapeFooter]}>
                <TouchableOpacity style={[styles.clearButton, isLandscape && styles.landscapeClearButton]} onPress={clearStrokes}
                    accessibilityRole="button"
                    accessibilityLabel="Clear"
                    accessibilityHint="Clears all strokes from the canvas"
                >
                    <Text style={[styles.clearButtonText, isLandscape && styles.landscapeClearButtonText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.submitButton, isLandscape && styles.landscapeSubmitButton, completedStrokes.length === 0 && styles.disabledButton]}
                    onPress={handleFinish}
                    disabled={completedStrokes.length === 0}
                    accessibilityRole="button"
                    accessibilityLabel="Done"
                    accessibilityHint="Submits your tracing work"
                    accessibilityState={{ disabled: completedStrokes.length === 0 }}
                >
                    <Text style={[styles.submitButtonText, isLandscape && styles.landscapeSubmitButtonText]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    header: {
        padding: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#1E293B',
        marginBottom: 4,
    },
    instructions: {
        fontSize: 16,
        color: '#64748B',
    },
    canvasWrapper: {
        flex: 1,
        position: 'relative',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        gap: 12,
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
    },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
    clearButton: {
        flex: 1,
        height: 56,
        minHeight: 56,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    clearButtonText: {
        color: '#64748B',
        fontSize: 16,
        fontWeight: '600',
    },
    submitButton: {
        flex: 2,
        height: 64,
        backgroundColor: '#22c55e',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    disabledButton: {
        backgroundColor: '#D1D5DB',
        shadowOpacity: 0,
        elevation: 0,
    },
    submitButtonText: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '900',
    },
    landscapeHeader: { padding: 10 },
    landscapeTitle: { fontSize: 18, marginBottom: 0 },
    landscapeFooter: { padding: 10, gap: 8 },
    landscapeClearButton: { height: 56, minHeight: 56 },
    landscapeClearButtonText: { fontSize: 14 },
    landscapeSubmitButton: { height: 56, minHeight: 56, borderRadius: 12 },
    landscapeSubmitButtonText: { fontSize: 18 },
});
